"use client";

import { useState } from "react";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Button } from "@/core/components/ui/button";
import { ConfirmDialog } from "@/core/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { EllipsisVertical, Pin, Trash2 } from "lucide-react";
import type { Conversation } from "../types";
import { useTranslation } from "react-i18next";
import { resolveDisplayTextForAction } from "../utils/chatbotUtils";
import { IconlyMenuOpenRight } from "@/core/assets/icons";
import { DS_COLOR, DS_TYPOGRAPHY } from "@/core/lib/ds-typography";
import {
  HistoryEditIcon,
  HistoryMoreIcon,
  HistorySidebarOpenIcon,
} from "./history-panel-icons";
import { IconlyPenSquareBreak } from "@/core/assets/icons/iconly-pen-square-break";

type ConversationHistoryPanelProps = {
  conversations: Conversation[];
  activeConversationId?: string | null;
  onNewConversation: () => void;
  onSelectConversation: (conversationId: string) => void;
  onRemoveConversation: (conversationId: string) => void | Promise<void>;
  /** Quando presente, exibe o botão X para fechar o painel (ex: Centro de ações). */
  onClose?: () => void;
  /**
   * `action-center`: visual do "Menu-ia" do Figma (6051:2746) no Centro de ações.
   * As demais telas (janela flutuante, painel lateral) seguem no `default`.
   */
  variant?: "default" | "action-center";
};

// 30 caracteres: acima disso o título empurrava o menu de ações (3 pontinhos)
// para fora do card no painel do Centro de ações.
function truncateConversationTitle(title: string, maxLength = 30) {
  const normalizedTitle = title.trim();
  if (normalizedTitle.length <= maxLength) return normalizedTitle;
  return `${normalizedTitle.slice(0, maxLength)}...`;
}

export function ConversationHistoryPanel({
  conversations,
  activeConversationId,
  onNewConversation,
  onSelectConversation,
  onRemoveConversation,
  onClose,
  variant = "default",
}: ConversationHistoryPanelProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [conversationIdToRemove, setConversationIdToRemove] = useState<
    string | null
  >(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [translate] = useTranslation(["core"]);

  // Lista única sob "Histórico" (Figma Menu-ia): o nome da conversa já diz do
  // que se trata e a Bruna é uma só, então o agrupamento por modo saiu.
  const displayTitle = (c: Conversation) =>
    resolveDisplayTextForAction(
      c.title,
      (key) => translate(key) as string | undefined,
    ) ?? c.title;

  const confirmDialog = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={(open) => {
        setConfirmOpen(open);
        if (!open) setConversationIdToRemove(null);
      }}
      title={translate("chatbot-history-remove-dialog-title") as string}
      description={
        translate("chatbot-history-remove-dialog-description") as string
      }
      confirmLabel={translate("chatbot-history-remove-confirm") as string}
      cancelLabel={translate("chatbot-history-remove-cancel") as string}
      confirmVariant="destructive"
      confirmDisabled={!conversationIdToRemove || isRemoving}
      onConfirm={async () => {
        if (!conversationIdToRemove) return;
        setIsRemoving(true);
        try {
          await onRemoveConversation(conversationIdToRemove);
        } finally {
          setIsRemoving(false);
        }
      }}
    />
  );

  const removeMenu = (c: Conversation, trigger: React.ReactNode) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        className="shadow-sm rounded-lg"
      >
        <DropdownMenuItem
          className="gap-2 items-center text-sm text-danger-500 [&_svg]:text-danger-500 focus:bg-[#EBEBEB] focus:text-danger-500 data-[highlighted]:bg-[#EBEBEB] data-[highlighted]:text-danger-500"
          disabled={isRemoving}
          onClick={(e) => {
            e.stopPropagation();
            setConversationIdToRemove(c.id);
            setConfirmOpen(true);
          }}
        >
          <Trash2 className="w-3 h-3 mt-[-3px]" />
          {translate("chatbot-history-delete-item")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (variant === "action-center") {
    return (
      <div
        className="box-border flex h-full w-full min-w-0 flex-col gap-4 p-6"
        data-testid="conversation-history-panel"
      >
        {/* Header: "Nova conversa" (Sidebar/Menu Item com lápis) + recolher */}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onNewConversation}
            className="flex min-h-9 min-w-0 flex-1 items-center gap-2 rounded-full px-3 py-2 text-left hover:bg-[var(--woba-color-surface-common-hover)]"
            style={{
              ...DS_TYPOGRAPHY.labelXsmall,
              color: DS_COLOR.contentSubtle,
            }}
            aria-label={translate("chatbot-history-new-conversation") as string}
          >
            <HistoryEditIcon color={DS_COLOR.contentSubtle} />
            <span className="truncate">
              {translate("chatbot-history-new-conversation")}
            </span>
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-[var(--woba-color-surface-common-hover)]"
              aria-label={translate("chatbot-history-close") as string}
            >
              <HistorySidebarOpenIcon color={DS_COLOR.contentSubtle} />
            </button>
          )}
        </div>

        <span
          aria-hidden
          className="h-px w-full shrink-0"
          style={{ backgroundColor: DS_COLOR.strokeDefault }}
        />

        <div className="flex min-h-0 flex-1 flex-col gap-1">
          <p
            className="flex h-8 shrink-0 items-center px-3 pb-2 pt-3"
            style={{
              ...DS_TYPOGRAPHY.overlineMedium,
              color: DS_COLOR.contentSubtle,
            }}
          >
            {translate("chatbot-history-section-title")}
          </p>
          {/* O Viewport do Radix embrulha o conteúdo num div `display: table`
              (min-width 100%), que crescia até a largura do título mais longo:
              a pílula passava da borda do painel, o "···" sumia e o texto não
              ganhava reticências. Forçar `block` prende a lista à largura útil. */}
          <ScrollArea
            className="min-h-0 min-w-0 flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block"
            data-testid="conversation-history-scroll"
          >
            {conversations.length === 0 ? (
              <p
                className="px-3 py-2"
                style={{
                  ...DS_TYPOGRAPHY.captionMedium,
                  color: DS_COLOR.contentSubtle,
                }}
              >
                {translate("chatbot-history-empty")}
              </p>
            ) : (
              <ul className="flex flex-col">
                {conversations.map((c) => {
                  const isActive = c.id === activeConversationId;
                  const title = displayTitle(c);
                  return (
                    <li key={c.id}>
                      <div
                        className={`box-border flex min-h-9 w-full min-w-0 items-center gap-2 rounded-full px-3 py-2 ${
                          isActive
                            ? ""
                            : "hover:bg-[var(--woba-color-surface-common-hover)]"
                        }`}
                        style={{
                          backgroundColor: isActive
                            ? DS_COLOR.surfaceActive
                            : undefined,
                        }}
                        data-active={isActive}
                      >
                        <button
                          type="button"
                          onClick={() => onSelectConversation(c.id)}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                          aria-label={
                            translate("chatbot-history-open-conversation", {
                              title,
                            }) as string
                          }
                        >
                          {c.pinned && (
                            <Pin className="size-4 shrink-0 text-aiBlue-700" />
                          )}
                          <span
                            className="min-w-0 flex-1 truncate"
                            data-testid="conversation-history-title"
                            style={{
                              ...DS_TYPOGRAPHY.labelXsmall,
                              color: isActive
                                ? DS_COLOR.contentActive
                                : DS_COLOR.contentSubtle,
                            }}
                          >
                            {title}
                          </span>
                        </button>
                        {removeMenu(
                          c,
                          <button
                            type="button"
                            disabled={isRemoving}
                            className="flex size-9 shrink-0 items-center justify-center rounded-full hover:bg-[var(--woba-color-surface-common-hover)] disabled:opacity-50"
                            aria-label={
                              translate("chatbot-history-open-menu") as string
                            }
                            onClick={(e) => e.stopPropagation()}
                          >
                            <HistoryMoreIcon
                              size={16}
                              color={DS_COLOR.contentSubtle}
                            />
                          </button>,
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>
        </div>
        {confirmDialog}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header do painel de histórico */}
      <div className="flex shrink-0 items-center justify-between px-6 py-4">
        <div className="flex justify-between items-center gap-2 border-b border-border w-full pb-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onNewConversation}
            className="text-sm text-[#18181B] hover:text-warmgray-500 py-2 px-0 hover:bg-transparent flex items-center"
            aria-label={translate("chatbot-history-new-conversation") as string}
          >
            <IconlyPenSquareBreak size={18} color="currentColor" />{" "}
            <span className="ml-2">
              {translate("chatbot-history-new-conversation")}
            </span>
          </Button>
          <div className="flex items-center gap-1">
            {onClose && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 text-[#18181B] hover:text-foreground"
                aria-label={translate("chatbot-history-close") as string}
              >
                <IconlyMenuOpenRight size={18} color="currentColor" />
              </Button>
            )}
          </div>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-4 flex flex-col gap-4">
          {conversations.length === 0 ? (
            <div className="text-sm text-darkgray-600 px-2">
              {translate("chatbot-history-empty")}
            </div>
          ) : (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 mb-2 mt-3">
                {translate("chatbot-history-section-title")}
              </p>
              <ul className="flex flex-col gap-1">
                {conversations.map((c) => (
                  <li key={c.id}>
                    <div
                      className={`group flex items-center justify-between gap-2 ${c.id === activeConversationId ? "bg-neutral-200 rounded-md" : ""}`}
                    >
                      <button
                        type="button"
                        onClick={() => onSelectConversation(c.id)}
                        className="flex-1 text-left p-2 min-w-0 rounded-md"
                        aria-label={
                          translate("chatbot-history-open-conversation", {
                            title: displayTitle(c),
                          }) as string
                        }
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {c.pinned && (
                            <Pin className="w-4 h-4 text-aiBlue-700" />
                          )}
                          <span className="truncate text-sm font-semibold text-darkgray-600">
                            {truncateConversationTitle(displayTitle(c))}
                          </span>
                        </div>
                      </button>

                      <div className="pr-2">
                        {removeMenu(
                          c,
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={isRemoving}
                            className="h-8 w-8 p-0 text-darkgray-500 hover:bg-transparent"
                            aria-label={
                              translate("chatbot-history-open-menu") as string
                            }
                            onClick={(e) => e.stopPropagation()}
                          >
                            <EllipsisVertical className="w-4 h-4" />
                          </Button>,
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ScrollArea>
      {confirmDialog}
    </div>
  );
}
